import * as React from 'react';
import { RefreshCw, TriangleAlert } from 'lucide-react';
import {
  formatMarginWithSource,
  MarginSource,
  RateUnit,
  type RateUnit as RateUnitCode,
  type RateWarning,
} from '@clawhire/shared';
import { cn } from '@/lib/utils';
import { EMPTY, enumLabel, enumOptions, formatPct, formatRate } from '@/lib/format';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { SimpleSelect } from '@/components/ui/select';
import { SimpleTooltip } from '@/components/ui/tooltip';
import { toast } from '@/components/ui/toaster';
import { formatPct1 } from '@/pages/submissions/helpers';
import type { SubmissionDetail, SubmissionPatch } from '@/pages/submissions/types';

export interface RateBlockProps {
  submission: SubmissionDetail;
  canEdit: boolean;
  /** `PATCH /submissions/:id`; rejects on error (shown as a toast here). */
  onPatch: (patch: SubmissionPatch) => Promise<unknown>;
  /** `POST /submissions/:id/margin` with `null` = use position/global. */
  onMargin: (marginPct: number | null) => Promise<unknown>;
  /** `POST /submissions/:id/recalculate-offer` (Screening only). */
  onRecalculate: () => Promise<unknown>;
  recalculating?: boolean;
}

const RATE_UNIT_OPTIONS = enumOptions(RateUnit);

/**
 * Spec §5 on one submission: candidate rate, effective margin (with its source and an override),
 * client offer with the suggested value, actual margin, supplier ceiling and the rate warnings.
 */
export function RateBlock({
  submission: s,
  canEdit,
  onPatch,
  onMargin,
  onRecalculate,
  recalculating = false,
}: RateBlockProps) {
  const { rates } = s;
  const find = (code: RateWarning['code']) => rates.warnings.find((w) => w.code === code) ?? null;
  const aboveCeiling = find('CANDIDATE_ABOVE_CEILING');
  const belowTarget = find('ACTUAL_MARGIN_BELOW_TARGET');
  const marginChanged = rates.marginChangedSinceOffer
    ? (find('MARGIN_CHANGED_SINCE_OFFER')?.message ?? 'Margin changed since this offer was calculated.')
    : null;
  const isScreening = s.status === 'SCREENING';

  const save = (patch: SubmissionPatch) =>
    onPatch(patch).catch((e: unknown) => toast.error(e instanceof Error ? e.message : 'Could not save'));

  const recalcButton = (
    <Button
      type="button"
      variant="outline"
      size="sm"
      disabled={!canEdit || !isScreening || recalculating}
      loading={recalculating}
      onClick={() => void onRecalculate()}
    >
      <RefreshCw aria-hidden="true" /> Recalculate offer
    </Button>
  );

  return (
    <dl className="grid gap-x-8 gap-y-5 sm:grid-cols-2">
      <Row label="Candidate rate">
        <div className="flex flex-wrap items-center gap-2">
          <NumberField
            id="rate-candidate"
            label="Candidate rate"
            value={s.candidateRateEur}
            onSave={(v) => save({ candidateRateEur: v })}
            disabled={!canEdit}
            suffix="EUR"
          />
          <SimpleSelect
            aria-label="Candidate rate unit"
            value={s.candidateRateUnit}
            options={RATE_UNIT_OPTIONS}
            onChange={(v) => {
              if (v && RateUnit.is(v) && v !== s.candidateRateUnit)
                void save({ candidateRateUnit: v as RateUnitCode });
            }}
            disabled={!canEdit}
            className="h-8 w-32"
          />
        </div>
        {s.candidateRateUnit === 'DAY' && rates.candidateRateH !== null ? (
          <Hint>
            = {formatRate(rates.candidateRateH)} at {rates.hoursPerDay} h/day
          </Hint>
        ) : null}
        {s.candidate.supplier ? <Hint>Supplier: {s.candidate.supplier.name}</Hint> : null}
        {aboveCeiling ? <Alert>{aboveCeiling.message}</Alert> : null}
      </Row>

      <Row label="Supplier ceiling">
        <div className={cn('text-sm tabular-nums', aboveCeiling && 'font-medium text-destructive')}>
          {formatRate(rates.supplierCeiling)}
          {rates.supplierCeilingDisplay !== null ? (
            <span className="ml-1 text-xs text-muted-foreground">
              (shown as {formatRate(rates.supplierCeilingDisplay)})
            </span>
          ) : null}
        </div>
        <Hint>
          Client {formatRate(s.position.clientRateEur, s.position.clientRateUnit ?? 'HOUR')} × (1 − effective
          margin), the most a supplier can be paid.
        </Hint>
      </Row>

      <Row label="Effective margin">
        <div className="text-sm font-medium">{formatMarginWithSource(rates.effectiveMargin)}</div>
        <div className="flex flex-wrap items-center gap-2">
          <NumberField
            id="rate-margin-override"
            label="Margin override for this submission"
            value={s.marginPct}
            onSave={(v) => onMargin(v)}
            disabled={!canEdit}
            suffix="%"
            max={99.99}
            placeholder="override"
          />
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={!canEdit || s.marginPct === null}
            onClick={() => void onMargin(null)}
          >
            Use position/global
          </Button>
        </div>
        <Hint>
          Position{' '}
          {s.position.marginPct !== null && s.position.marginPct !== undefined
            ? formatPct(s.position.marginPct)
            : 'not set'}{' '}
          · an override applies to this submission only.
        </Hint>
      </Row>

      <Row label="Margin applied">
        <div className="text-sm tabular-nums">
          {s.marginPctApplied !== null ? (
            <>
              {formatPct(s.marginPctApplied)}{' '}
              <span className="text-muted-foreground">({enumLabel(MarginSource, s.marginSource)})</span>
            </>
          ) : (
            EMPTY
          )}
        </div>
        <Hint>Snapshot of the margin and its source when the offer was calculated.</Hint>
      </Row>

      <Row label="Client offer">
        <div className="flex flex-wrap items-center gap-2">
          <NumberField
            id="rate-client-offer"
            label="Client offer"
            value={s.clientRateEur}
            onSave={(v) => save({ clientRateEur: v })}
            disabled={!canEdit}
            suffix="EUR/h"
          />
          {rates.clientOffer !== null ? (
            <span className="text-xs text-muted-foreground">
              suggested {formatRate(rates.clientOffer)}
              {s.clientRateEur === null ? ' (in use)' : ''}
            </span>
          ) : null}
          {isScreening ? (
            recalcButton
          ) : (
            <SimpleTooltip label="A sent offer is fixed">
              <span className="inline-flex" tabIndex={0}>
                {recalcButton}
              </span>
            </SimpleTooltip>
          )}
        </div>
        {marginChanged ? (
          <p role="status" className="text-xs text-warning">
            {marginChanged}
            {!isScreening ? ' A sent offer is fixed; adjust the offer by hand if needed.' : ''}
          </p>
        ) : null}
      </Row>

      <Row label="Actual margin">
        <div
          className={cn(
            'inline-flex items-center gap-1 text-sm font-medium tabular-nums',
            belowTarget && 'text-destructive',
          )}
        >
          {belowTarget ? <TriangleAlert className="h-4 w-4" aria-hidden="true" /> : null}
          {formatPct1(rates.actualMarginPct)}
        </div>
        {belowTarget ? (
          <Alert>{belowTarget.message}</Alert>
        ) : (
          <Hint>(offer − candidate rate) ÷ offer, on the offer in use.</Hint>
        )}
      </Row>
    </dl>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</dt>
      <dd className="mt-1 space-y-1">{children}</dd>
    </div>
  );
}

function Hint({ children }: { children: React.ReactNode }) {
  return <p className="text-xs text-muted-foreground">{children}</p>;
}

function Alert({ children }: { children: React.ReactNode }) {
  return (
    <p role="alert" className="flex items-start gap-1 text-xs font-medium text-destructive">
      <TriangleAlert className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
      <span>{children}</span>
    </p>
  );
}

interface NumberFieldProps {
  id: string;
  label: string;
  value: number | null;
  onSave: (value: number | null) => unknown;
  disabled?: boolean;
  suffix?: string;
  min?: number;
  max?: number;
  step?: number;
  placeholder?: string;
}

/** Number input that saves on blur/Enter when the value changed; Esc restores the saved value. */
function NumberField({
  id,
  label,
  value,
  onSave,
  disabled,
  suffix,
  min = 0,
  max,
  step = 0.01,
  placeholder,
}: NumberFieldProps) {
  const toText = (v: number | null) => (v === null ? '' : String(v));
  const [draft, setDraft] = React.useState(() => toText(value));
  React.useEffect(() => setDraft(toText(value)), [value]);

  const commit = () => {
    const text = draft.trim().replace(',', '.');
    if (text === '') {
      if (value !== null) onSave(null);
      return;
    }
    const next = Number(text);
    if (!Number.isFinite(next)) {
      setDraft(toText(value));
      return;
    }
    if (next !== value) onSave(next);
  };

  return (
    <span className="inline-flex items-center gap-1.5">
      <Input
        id={id}
        type="number"
        inputMode="decimal"
        aria-label={label}
        value={draft}
        min={min}
        max={max}
        step={step}
        placeholder={placeholder}
        disabled={disabled}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            e.currentTarget.blur();
          } else if (e.key === 'Escape') {
            setDraft(toText(value));
          }
        }}
        className="h-8 w-28 tabular-nums"
      />
      {suffix ? <span className="text-sm text-muted-foreground">{suffix}</span> : null}
    </span>
  );
}
